import { Inject, Injectable, Injector, TemplateRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseError } from '@angular/fire/app';
import { UILoginService } from 'ngx-zcs-auth';
import { UiPromiseService, ToastFacade, DialogDataComponent } from 'ngx-zcs-components';
import { AuthenticationService, wait } from 'ngx-core-services';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

import { errorHandler } from './firebase.error';

@Injectable({
  providedIn: 'root',
})
export class LoginService implements UILoginService {

  private get uiPromiseService() {
    return this.injector.get(UiPromiseService);
  }

  private get authenticationService() {
    return this.injector.get(AuthenticationService);
  }

  private get toastFacade() {
    return this.injector.get(ToastFacade);
  }

  private get dialog() {
    return this.injector.get(MatDialog);
  }

  constructor(
    @Inject(Injector) private readonly injector: Injector,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    readonly translateService: TranslateService,
  ) {}

  async resendEmailVerification(): Promise<void> {
    await this.authenticationService.sendEmailVerification();
    await this.toastFacade.present(
      this.translateService.instant('i18n.LoginService.verificationEmailDescription'),
      this.translateService.instant('i18n.LoginService.verificationEmailTitle'),
      {
        type: 'browser',
        severity: 'success',
        browserMode: 'simple',
      },
    );
  }

  async onLogin(login: NgForm, resendEmail: TemplateRef<any>): Promise<void> {
    if (login.invalid) {
      return;
    }
    login.form.markAsPending();
    const { email, password } = login.control.value;
    const result = await this.uiPromiseService.run(
      async () => {
        const rlogin = await this.authenticationService.loginWithEmailAndPassword(email, password).catch((error) => {
          const errorMessage =  errorHandler(error as FirebaseError);
          this.toastFacade.present(errorMessage, this.translateService.instant('i18n.LoginService.loginFailed'), {
              severity: 'error',
              browserMode: 'persistent',
            },
          );

          login.reset();
        });
        if (!rlogin) {
          return ;
        }

        const isNotEmailVerified = !this.authenticationService.isEmailVerified(rlogin);
        if (isNotEmailVerified && resendEmail) {
          await this.resendEmailVerification().catch(() => {});
          login.reset();

          const dialogRef = this.dialog.open(DialogDataComponent, {
            width: '600px',
          });

          const actions = [
            {
              text: this.translateService.instant('i18n.Common.iUnderstand'),
              cssClass: 'custom-mat-button-primary',
              focus: true,
              action: () => {
                dialogRef.close();
              },
            },
          ];

          dialogRef.componentInstance.data = {
            title: this.translateService.instant('i18n.LoginService.emailNotVerifiedTitle'),
            content: this.translateService.instant('i18n.LoginService.emailNotVerifiedDescription'),
            buttons: actions,
          };

          return await this.authenticationService.signOut();
        }

        await this.toastFacade.present(
          this.translateService.instant(this.translateService.instant('i18n.LoginService.welcome',{ email: rlogin.user.email })),
          this.translateService.instant('i18n.Common.successful'),
          {
            type: 'browser',
            browserMode: 'simple',
            severity: 'success',
          },
        );

        await this.router.navigate(['../../'], { relativeTo: this.route, replaceUrl: true });
        await wait(2000);
      },
      {
        loading: true,
        errorHandler: async (error) => {
          const errorMessage =  errorHandler(error as FirebaseError);
          await this.toastFacade.present(
            errorMessage,
            this.translateService.instant('i18n.LoginService.loginFailed'),
            {
              severity: 'error',
              browserMode: 'persistent',
            },
          );
          login.reset();
        },
      },
    );
  }
}
